import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Deal, DocumentRecord } from '@bcc/shared';
import { apiFetch } from '../lib/api';
import { storageOrAbsoluteCoverUrl } from '../lib/dealCover';
import { InvestCheckout } from '../components/InvestCheckout';
import { useAuth } from '../contexts/AuthContext';
import { envConfig } from '../env';

export function DealDetail() {
  const { id } = useParams();
  const { session, profile } = useAuth();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [amount, setAmount] = useState<number>(25000);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const token = session?.access_token;

  const cover = useMemo(() => storageOrAbsoluteCoverUrl(deal?.image_paths), [deal?.image_paths]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch<{ deal: Deal }>(`/api/deals/${id}`, {
          token,
        });
        if (!cancelled) {
          setDeal(res.deal);
          setAmount(Number(res.deal.min_investment));
        }
        if (token) {
          const d = await apiFetch<{ documents: DocumentRecord[] }>(
            `/api/deals/${id}/documents`,
            { token }
          );
          if (!cancelled) setDocs(d.documents);
        }
      } catch (e) {
        if (!cancelled) setErr('Unable to load offering');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  async function startInvest() {
    if (!deal || !token) return;
    setErr(null);
    try {
      const res = await apiFetch<{
        investment: unknown;
        clientSecret: string | null;
      }>('/api/investments', {
        method: 'POST',
        body: JSON.stringify({ deal_id: deal.id, amount }),
        token,
      });
      setClientSecret(res.clientSecret ?? null);
    } catch {
      setErr('Could not initiate investment — verify minimum amount and onboarding status.');
    }
  }

  if (loading) return <div className="px-6 py-24 text-slate-400 text-sm">Loading…</div>;
  if (err || !deal)
    return (
      <div className="px-6 py-24 text-red-400 text-sm">
        {err ?? 'Deal not found — it may be unpublished or was removed.'}
      </div>
    );

  const approved = profile?.investor_approved;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7 space-y-6">
        <div className="rounded-3xl overflow-hidden border border-white/10 bg-slate-950/60">
          <div
            className={`h-52 w-full bg-gradient-to-br from-slate-800 to-slate-900 ${
              cover ? '' : ''
            }`}
            style={cover ? { backgroundImage: `url(${cover})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
          />
          <div className="p-8 space-y-4">
            <h1 className="text-3xl font-semibold text-white tracking-tight">{deal.title}</h1>
            <p className="text-sm leading-relaxed text-slate-300 whitespace-pre-line">{deal.description}</p>
          </div>
        </div>
      </div>
      <aside className="lg:col-span-5 space-y-8">
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-slate-200 uppercase tracking-[0.16em]">Highlights</h2>
          <dl className="space-y-4 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-400">Target IRR</dt>
              <dd className="text-white font-semibold">
                {deal.irr != null ? `${Number(deal.irr).toFixed(1)}%` : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-400">Equity multiple</dt>
              <dd className="text-white font-semibold">
                {deal.equity_multiple != null ? `${Number(deal.equity_multiple).toFixed(2)}x` : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-400">Minimum</dt>
              <dd className="text-white font-semibold">${Number(deal.min_investment).toLocaleString()}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-6 space-y-4">
          {!token ? (
            <p className="text-sm text-slate-300">Sign in as an approved investor to subscribe to this offering.</p>
          ) : !approved ? (
            <p className="text-sm text-amber-200">
              Your investor profile is pending compliance review. Staff will notify you when you can wire or pay by card.
            </p>
          ) : (
            <>
              {!clientSecret ? (
                <>
                  <label className="block text-sm space-y-2">
                    <span className="text-slate-300">Commitment amount (USD)</span>
                    <input
                      type="number"
                      min={deal.min_investment}
                      step={1000}
                      value={amount}
                      onChange={(e) => setAmount(Number(e.target.value))}
                      className="w-full rounded-xl bg-slate-900/70 border border-white/10 px-3 py-2"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => void startInvest()}
                    className="w-full px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-medium"
                  >
                    Invest securely
                  </button>
                  {!envConfig.stripePublishableKey ? (
                    <p className="text-[11px] text-amber-200">
                      Add Stripe publishable + secret keys in env to activate card funding.
                    </p>
                  ) : null}
                </>
              ) : (
                <InvestCheckout
                  clientSecret={clientSecret}
                  onComplete={() => {
                    window.location.href = '/dashboard';
                  }}
                />
              )}
            </>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-5">
          <h3 className="text-xs uppercase tracking-[0.16em] text-slate-400 mb-3">Data room</h3>
          {docs.length === 0 ? (
            <p className="text-sm text-slate-500">
              Supporting documents haven’t been wired for this corridor yet — upload collateral from the Staff console.
            </p>
          ) : (
            <ul className="space-y-2 text-sm text-blue-200">
              {docs.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    className="hover:underline"
                    onClick={async () => {
                      const res = await apiFetch<{ signedUrl: string }>(
                        `/api/documents/signed-url?documentId=${encodeURIComponent(d.id)}`,
                        { token }
                      );
                      window.open(res.signedUrl, '_blank', 'noopener');
                    }}
                  >
                    {d.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
